// RGB / WWH / WWRGB dimmable light. One VERTICAL 0-255 slider per channel, all
// always visible side by side (bottom = 0, top = 255). Publishes the comma-joined
// channel values on release; status is the same comma format (e.g. WWRGB
// 'w,ww,r,g,b'). Covers all three variants:
//   DeviceID 25 → White / Warm (2ch)
//   DeviceID 24 → R / G / B (3ch)
//   DeviceID 23 → W / WW / R / G / B (5ch)

import React, { useEffect, useState } from 'react';
import { Box, Slider, Typography, Stack, Button } from '@mui/material';
import DeviceShell from '../../components/DeviceShell';
import { useApp } from '../../state/AppContext';
import { useDeviceStatus } from '../../mqtt/useDeviceStatus';
import { publishCommand } from '../../utils/deviceController';

// Colour each channel's slider so it reads at a glance.
function channelColor(ch) {
  const c = String(ch).toLowerCase();
  if (c === 'r' || c.startsWith('red')) return '#e53935';
  if (c === 'g' || c.startsWith('green')) return '#43a047';
  if (c === 'b' || c.startsWith('blue')) return '#1e88e5';
  if (c === 'w' || c === 'white') return '#e0e0e0';
  if (c === 'ww' || c === 'wh' || c.startsWith('warm')) return '#ffb300';
  return '#90caf9';
}

// Quick presets per channel signature — exact comma values the firmware expects.
function presetsFor(channels) {
  const n = channels.length;
  if (n === 2) {
    return [
      { label: 'White', values: [255, 0] },
      { label: 'Warm', values: [0, 255] },
      { label: 'Cool', values: [128, 128] },
      { label: 'Off', values: [0, 0] },
    ];
  }
  if (n === 5) {
    return [
      { label: 'White', values: [255, 0, 0, 0, 0] },
      { label: 'Warm', values: [0, 255, 0, 0, 0] },
      { label: 'Red', values: [0, 0, 255, 0, 0] },
      { label: 'Green', values: [0, 0, 0, 255, 0] },
      { label: 'Blue', values: [0, 0, 0, 0, 255] },
      { label: 'Off', values: [0, 0, 0, 0, 0] },
    ];
  }
  return [
    { label: 'Red', values: [255, 0, 0] },
    { label: 'Green', values: [0, 255, 0] },
    { label: 'Blue', values: [0, 0, 255] },
    { label: 'Off', values: [0, 0, 0] },
  ];
}

export default function LightScreen({ device, channels, initialStatus = '' }) {
  const { userId } = useApp();
  const uno = device.UniqueNumber;
  const [status] = useDeviceStatus(uno, initialStatus);
  const [values, setValues] = useState(() => channels.map(() => 0));

  useEffect(() => {
    if (status) {
      const parts = status.split(',').map((n) => Number(n));
      if (parts.length >= channels.length && parts.every((n) => !Number.isNaN(n))) {
        setValues(parts.slice(0, channels.length));
      }
    }
  }, [status, channels.length]);

  const publish = (vals) => publishCommand(uno, vals.join(','), userId);

  const onChange = (i, v) => {
    setValues((prev) => { const u = [...prev]; u[i] = v; return u; });
  };
  const commit = (i, v) => {
    const u = [...values];
    u[i] = v;
    publish(u);
  };

  const applyPreset = (vals) => { setValues(vals); publish(vals); };

  const presets = presetsFor(channels);

  return (
    <DeviceShell title={device.DeviceName || uno}>
      <Stack spacing={2} alignItems="center">
        {/* Vertical channel sliders — always visible, side by side. */}
        <Box sx={{ display: 'flex', gap: 2.5, justifyContent: 'center', alignItems: 'flex-end', py: 1 }}>
          {channels.map((ch, i) => (
            <Box key={ch} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="caption" color="text.primary" sx={{ minHeight: 16 }}>{values[i]}</Typography>
              <Slider
                orientation="vertical"
                min={0} max={255} value={values[i]}
                onChange={(_, v) => onChange(i, v)}
                onChangeCommitted={(_, v) => commit(i, v)}
                sx={{
                  height: 200, color: channelColor(ch),
                  '& .MuiSlider-rail': { opacity: 0.25 },
                  '& .MuiSlider-thumb': { width: 18, height: 18 },
                }}
              />
              <Typography variant="caption" fontWeight={700} color="text.primary">{ch}</Typography>
            </Box>
          ))}
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="center">
          {presets.map((p) => (
            <Button key={p.label} size="small" variant="outlined" onClick={() => applyPreset(p.values)}>
              {p.label}
            </Button>
          ))}
        </Stack>
      </Stack>
    </DeviceShell>
  );
}
